/**
 * Alkahest image generation provider (OpenAI-compatible)
 */

import { BaseProvider } from '../../core/base-provider';
import { ImageGenerationOptions, ImageResponse, UsageStats, ProviderError } from '../../core/types';

export interface AlkahestImageProvider extends BaseProvider {
  generate(prompt: string, options?: ImageGenerationOptions): Promise<ImageResponse>;
}

export class AlkahestImage extends BaseProvider implements AlkahestImageProvider {
  private baseUrl: string = '';
  private apiKey?: string;

  constructor() {
    super('alkahest-image', 'Alkahest Image', 'image');
  }

  protected async onInitialize(): Promise<void> {
    this.baseUrl = this.config.baseUrl || 'http://localhost:9999';
    this.apiKey = this.config.apiKey;

    if (!this.apiKey && !this.config.privyToken) {
      throw new ProviderError('Alkahest API key or Privy token is required', this.id);
    }
  }

  protected async checkAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/models`, {
        headers: this.getHeaders(),
      });

      if (!response.ok) return false;

      const data = await response.json();
      // Check if any image models are available
      return (
        (data as any).data?.some(
          (model: any) =>
            model.id.includes('dall-e') ||
            model.id.includes('stable-diffusion') ||
            model.capabilities?.includes('image')
        ) || false
      );
    } catch {
      return false;
    }
  }

  protected async fetchUsage(): Promise<UsageStats> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/mana/balance`, {
        headers: this.getHeaders(),
      });

      if (!response.ok) return { tokensUsed: 0, requestsCount: 0, cost: 0 };

      const data = await response.json();
      return {
        remaining: (data as any).balance,
        tokensUsed: 0,
        requestsCount: 0,
        cost: 0,
      };
    } catch {
      return { tokensUsed: 0, requestsCount: 0, cost: 0 };
    }
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    } else if (this.config.privyToken) {
      headers['Authorization'] = `Bearer ${this.config.privyToken}`;
    }

    return headers;
  }

  async generate(prompt: string, options?: ImageGenerationOptions): Promise<ImageResponse> {
    this.ensureInitialized();

    try {
      const response = await fetch(`${this.baseUrl}/api/v1/images/generations`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          prompt,
          n: options?.n || 1,
          size: options?.size || '1024x1024',
          model: options?.model || 'dall-e-3',
          quality: options?.quality || 'standard',
          style: options?.style || 'vivid',
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));

        if (response.status === 429) {
          throw new ProviderError('Rate limit exceeded', this.id, {
            retryAfter: response.headers.get('X-RateLimit-Reset'),
          });
        }

        if (response.status === 402) {
          throw new ProviderError('Insufficient mana balance', this.id, error);
        }

        throw new ProviderError(
          (error as any).error?.message || (error as any).error || 'Failed to generate image',
          this.id,
          error as any
        );
      }

      const data = await response.json();

      return {
        images: (data as any).data.map((item: any) => ({
          url: item.url,
          base64: item.b64_json,
          revisedPrompt: item.revised_prompt,
        })),
        model: options?.model || 'dall-e-3',
      };
    } catch (error: any) {
      if (error instanceof ProviderError) {
        throw error;
      }

      throw new ProviderError(error.message || 'Failed to generate image', this.id, error);
    }
  }
}
